import { Builder, BuilderComponent } from '@builder.io/react';
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import DefaultErrorPage from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { awaitBuilderData, registerComponents } from '../../../../../builder';
import BackToLatestArticle from '../../../../../components/back-to-latest-article/back-to-latest-article';
import { BreadcrumbItem } from '../../../../../components/breadcrumb/breadcrumb';
import ArticleBioBlock from '../../../../../components/business-article-bio-block/business-article-bio-block';
import ArticleCategoriesBlock from '../../../../../components/business-article-categories-block/business-article-categories-block';
import ArticleContributorBlock from '../../../../../components/business-article-contributor-block/business-article-contributor-block';
import BusinessSingleArticleHero from '../../../../../components/business-article-single-hero/business-article-single-hero';
import SocialAndShare from '../../../../../components/business-share-icons/business-share-icons';
import DefaultLayout from '../../../../../components/layouts/default-layout';
import MetaBlock from '../../../../../components/meta-block/MetaBlock';
import NewsletterSignUpSection from '../../../../../components/newsletter-signup-section/newsletter-signup-section';
import { NotificationAutoPadding } from '../../../../../components/notification-auto-padding/notification-auto-padding';
import RelatedArticles, { RelatedArticlesProps } from '../../../../../components/related-articles/related-articles';
import {
    getAllArticlePaths,
    getArticleFromID,
    getArticlePageFromUrl,
    getRelatedArticles,
    mapBuilderObjectToArticle,
    mapBuilderObjectToCategory,
} from '../../../../../helpers/BuilderHelper';
import { Locale, LOCALE_LINKS, localifyLink, stripLocaleFromURL, useLocale } from '../../../../../helpers/LangHelper';
import { Article } from '../../../../../types/Article';

registerComponents();

export async function getStaticProps({ params }: GetStaticPropsContext<any>) {
    const page = await getArticlePageFromUrl(params['article'], params['lang'], Builder.isPreviewing);
    const article = mapBuilderObjectToArticle(page);
    const relatedArticles = await getRelatedArticles(article, params['lang']);

    await awaitBuilderData(page, params['lang']);

    const { lang, business, category, article: articleSlug } = params;
    let pageSlug = stripLocaleFromURL([lang, business, 'access', category, articleSlug].join('/') ?? '');

    let canonicalUrl = null;
    const host = LOCALE_LINKS[lang as Locale]?.host;
    if (host) {
        canonicalUrl = `${host}/${lang}${pageSlug}`;
    }

    return {
        props: { article, relatedArticles, canonicalUrl },
        revalidate: 1800, // Half an hour
    };
}

export async function getStaticPaths() {
    const pages = await getAllArticlePaths();

    const allPaths = pages.map((link) => {
        return { params: link };
    });

    return {
        paths: allPaths,
        fallback: true,
    };
}

export default function Page(ctx: InferGetStaticPropsType<typeof getStaticProps>) {
    const { article, relatedArticles } = ctx;

    const router = useRouter();
    const { locale } = useLocale();

    const [stateArticle, setStateArticle] = useState<Article>(article);

    useEffect(() => {
        let cancelled = false;

        if ((Builder.isPreviewing || Builder.isEditing) && router.query['builder.overrides.article']) {
            getArticleFromID(router.query['builder.overrides.article'] as string).then((articleFromBuilder: any) => {
                if (articleFromBuilder && !cancelled) {
                    const mappedArticle = mapBuilderObjectToArticle(articleFromBuilder);

                    setStateArticle(mappedArticle);
                }
            });
        } else {
            if (article) {
                setStateArticle(article);
            }
        }

        return () => {
            cancelled = true;
        };
    }, [stateArticle, router.query]);

    useEffect(() => setStateArticle(article), [article]);

    if (router.isFallback) {
        return <h1>Loading...</h1>;
    }

    const isLive = !Builder.isEditing && !Builder.isPreviewing;
    if (!stateArticle && isLive) {
        return (
            <>
                <Head>
                    <meta name="robots" content="noindex" />
                    <meta name="title"></meta>
                </Head>
                <DefaultErrorPage statusCode={404} />
            </>
        );
    }

    if (!stateArticle) {
        return <h1>Loading...</h1>;
    }

    const leadCategory = mapBuilderObjectToCategory(article?.categories[0]);

    const relatedArticlesData: RelatedArticlesProps = {
        title: leadCategory?.name,
        description: leadCategory?.description,
        viewAllSeriesText: `View All Articles`,
        viewAllSeriesUrl: localifyLink(`/${locale}${leadCategory.categoryUrl}`, locale),
        articles: relatedArticles,
    };

    const breadcrumbItems: BreadcrumbItem[] = [
        { link: '/business/access', name: 'Access' },
        { link: leadCategory?.categoryUrl ?? '', name: leadCategory?.name ?? '' },
        { link: '', name: article?.title ?? '' },
    ];

    let targetUrl = `/${locale}${article.articleUrl}`;
    if (typeof window !== 'undefined') {
        targetUrl = `${window?.origin || ''}/${locale}${article.articleUrl}`;
    }

    return (
        <>
            <MetaBlock title={article.seoTitle ?? article.title} description={article.seoDescription} leadImage={article.leadImage} />

            <DefaultLayout>
                <NotificationAutoPadding />
                <BusinessSingleArticleHero article={article} breadcrumbs={breadcrumbItems} />
                <BuilderComponent model="article" content={stateArticle.builderModel} />
                <ArticleCategoriesBlock categories={stateArticle.categories} />
                {article.author && <ArticleBioBlock articleAuthor={stateArticle.author} />}
                {!!article.contributors?.length && <ArticleContributorBlock contributors={article.contributors} hasAuthorBefore={!!article.author?.name} />}
                <SocialAndShare shareUrl={targetUrl} />
                <BackToLatestArticle latestArticleUrl={localifyLink(`/${locale}/business/access`, locale)} />
                <RelatedArticles relatedArticles={relatedArticlesData} />
                <NewsletterSignUpSection />
            </DefaultLayout>
        </>
    );
}
